import { useEffect, useState } from "react";
import { Button, Checkbox, Dropdown, Form, Icon, Input, Message, Modal } from "semantic-ui-react";

import { ROLES, USER_STATUSES } from "models";
import "modules/Users/components/CreateUserModal/CreateUserModal.scss";
import { useGetFilterOptions } from "modules/Users/hooks";
import { useUsersCreate } from "modules/Users/service";
import { validateEmail } from "util/common";

type CreateUserFormProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const CreateUserForm = ({ isOpen, onClose }: CreateUserFormProps) => {
  const { roleOptions } = useGetFilterOptions();
  const { mutateAsync: createUserMutation, isPending } = useUsersCreate();

  const [role, setRole] = useState<ROLES | null>(null);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const resetState = () => {
    setRole(null);
    setEmail("");
    setStatus(false);
    setFormSubmitted(false);
  };

  const isMissingValue = () => !role || !email;
  const handleEmailChange = (evt: React.ChangeEvent<HTMLInputElement>) => setEmail(evt.target.value);
  const handleUserStatusChange = (status: boolean) => setStatus(status);

  const handleSaveUser = () => {
    setFormSubmitted(true);
    if (!role || !email || !validateEmail(email)) return;

    createUserMutation({ user: { email, role, status: status ? USER_STATUSES.Active : USER_STATUSES.Inactive } });
    setFormSubmitted(false);
    onClose();
  };

  useEffect(() => {
    if (isOpen) resetState();
  }, [isOpen]);

  return (
    <Modal className="CreateUserModal" size="tiny" closeOnDimmerClick open={isOpen} onClose={onClose}>
      <Modal.Header className="CreateUserModal__modal-header">
        <div className="CreateUserModal__modal-header">
          <p>Add User</p>
        </div>

        <div className="CreateUserModal__checkbox-container">
          <Checkbox checked={status} onChange={(_, { checked }) => handleUserStatusChange(!!checked)} label="Active" />
        </div>
      </Modal.Header>
      <Modal.Content>
        <Form style={{ padding: "0 7px" }}>
          <Form.Group>
            <Form.Field id="email" width={16} required>
              <label>Email</label>
              <Input value={email} fluid name="email" onChange={handleEmailChange} placeholder="Email" />
            </Form.Field>
          </Form.Group>
          <Form.Group style={{ marginBottom: "0" }}>
            <Form.Field id="role" width={16} required>
              <label>Role</label>
              <Dropdown
                selection
                selectOnBlur={false}
                options={roleOptions}
                placeholder="Select Role"
                value={Number(role)}
                onChange={(_, { value }) => setRole(value as ROLES)}
              />
            </Form.Field>
          </Form.Group>
        </Form>
        {(formSubmitted && isMissingValue()) || (email && !validateEmail(email)) ? (
          <Message error content={formSubmitted && isMissingValue() ? "All fields are required" : "Invalid email"} />
        ) : null}
      </Modal.Content>
      <Modal.Actions>
        <div className="CreateUserModal__modal-footer">
          <Button onClick={onClose} basic>
            Cancel
            <Icon className="xmark" />
          </Button>
          <Button color="green" disabled={isPending} loading={isPending} type="button" onClick={handleSaveUser}>
            Save
            <Icon className="check" />
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
